import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { clone } from 'ramda';
import { translator, selectors as magei18nSelectors } from 'mage-i18n';
import { VerticalRhythm, withResponsive, Row, Column } from 'mage-components';
import { generateCyElemId, isRunningContextCypress } from 'moet-test-utils/cypress/utils';
import { analytics, defaultContainerPropTypes, HorizontalWrapper, productsUtils } from 'moet-core';
import { ButtonPrimary, ButtonSecondary, SpacerHorizontal } from '@klarna/bubble-ui';
import { FLOW_STEP_PATHS } from '@/constants';
import * as a from '@/actions/signup';
import {
  getProducts as getProductsFromQuery,
  getSelectedCountry,
  getSelectedCountryKey,
  getSelectedPlatformKey,
  getSetupError,
  isKustomPartner,
  shouldShowContactSalesButton,
} from '@/selectors/data';
import { hasNonKlarnaOneProducts } from '@/selectors/merchant';
import { getSelectedPlatform } from '@/selectors/platforms';
import { getCompatibleProductsFlattened, isKlarnaOneAvailable } from '@/selectors/products';
import { ContainerTemplate, LinkWithoutHref, ProductSelectionWrapper } from '@/components';
import { ANALYTICS_EVENTS } from './analyticsEvents';
import {
  CY_MERCHANT_SIGNUP_PRODUCTS_CONTAINER_IDS,
  CY_PRODUCT_TO_CONTAINER_IDS_MAP,
} from './constants';

const tPrefix = 'products-fe.containers.signup_products';

export class SignupProductSelection extends React.Component {
  constructor(props) {
    super(props);

    const { products, selectedCountry, isKlarnaOneAvailable } = this.props;

    const isSelected = id =>
      props.productsFromQuery.includes(id) ||
      // preselects mandatory product on the page load
      productsUtils.isProductMandatory(id, selectedCountry.key, { isKlarnaOneAvailable });

    this.state = {
      products: products.reduce(
        (acc, product) => [
          ...acc,
          {
            id: product.id,
            isSelected: isSelected(product.id),
            group: product.id.startsWith('kp') ? 'kp' : product.id,
          },
        ],
        [],
      ),
    };

    this.onNext = this.onNext.bind(this);
    this.isSelectedFn = this.isSelectedFn.bind(this);
    this.onSelect = this.onSelect.bind(this);
  }

  onSelect(id) {
    const currentState = this.state.products.find(p => p.id === id);
    const { isKlarnaOneAvailable } = this.props;

    // it's from the current group and it's selected, it should remain selected
    // else should be unselected
    this.setState({
      products: this.state.products.map(product => ({
        ...product,
        isSelected:
          (product.id === id && !currentState.isSelected) ||
          (product.id !== id && product.group === currentState.group && product.isSelected) ||
          // mandatory product should always stay selected
          productsUtils.isProductMandatory(product.id, this.props.selectedCountry.key, {
            isKlarnaOneAvailable,
          }),
      })),
    });
  }

  isSelectedFn(id) {
    return this.state.products.find(p => p.id === id).isSelected;
  }

  onNext() {
    const { onNext, setProducts } = this.props;

    const state = clone(this.state);
    const productsFromState = state.products;

    const selectedProductsIds = productsFromState
      .filter(product => product.isSelected)
      .map(p => p.id);

    const productsForTracking = selectedProductsIds.join(', ');
    analytics.logEvent(ANALYTICS_EVENTS.selectProduct, { productId: productsForTracking });

    setProducts(selectedProductsIds);
    onNext();
  }

  renderButtons() {
    const { shouldShowContactSalesButton, history, isDisabled, isMobile, onBack, t } = this.props;

    const styles = {
      sales: {
        mobile: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end' },
        nonMobile: { display: 'flex', flexDirection: 'row', alignItems: 'center' },
      },
    };

    const navigationButtons = (
      <>
        <ButtonSecondary
          onClick={onBack}
          disabled={isDisabled}
          {...generateCyElemId(CY_MERCHANT_SIGNUP_PRODUCTS_CONTAINER_IDS.BTN_BACK, window)}
        >
          {t(`${tPrefix}.buttons.back`)}
        </ButtonSecondary>
        <SpacerHorizontal spaceToken="space/300" />
        <ButtonPrimary
          onClick={this.onNext}
          {...generateCyElemId(CY_MERCHANT_SIGNUP_PRODUCTS_CONTAINER_IDS.BTN_SUBMIT, window)}
        >
          {t(`${tPrefix}.buttons.next`)}
        </ButtonPrimary>
      </>
    );
    const salesButton = (
      <>
        <LinkWithoutHref
          onPress={e => {
            e.preventDefault();
            analytics.logEvent(ANALYTICS_EVENTS.selectContactSales);
            history.push('/products/' + FLOW_STEP_PATHS.CONTACT_DETAILS);
          }}
        >
          {t(`${tPrefix}.buttons.contact_sales`)}
        </LinkWithoutHref>
        <VerticalRhythm spacing={{ mobile: 5, tablet: 10 }} />
      </>
    );

    if (isMobile) {
      return (
        <>
          {shouldShowContactSalesButton && <div style={styles.sales.mobile}>{salesButton}</div>}
          <HorizontalWrapper display="flex" justifyContent="flex-end">
            {navigationButtons}
          </HorizontalWrapper>
        </>
      );
    }

    return (
      <Row>
        <Column columns={{ mobile: 12 }} alignItems="flex-end">
          <HorizontalWrapper display="flex">{navigationButtons}</HorizontalWrapper>
        </Column>
      </Row>
    );
  }

  render() {
    const {
      error,
      products,
      t,
      countryKey,
      setupError,
      hasNonKlarnaOneProductsSelected,
      isKlarnaOneAvailable,
      isKustomPartner,
    } = this.props;

    const kustomProducts = products.filter(p => p.id.startsWith('kco') && p.isCompatible);
    const hasKustomProducts = kustomProducts.length > 0;
    const klarnaProducts = products.filter(p => p.id.startsWith('kp') && p.isCompatible);

    let filteredProducts = products;
    if (isKustomPartner) {
      filteredProducts = hasKustomProducts ? kustomProducts : [];
    } else {
      filteredProducts = klarnaProducts;
    }
    const isKlarnaOne = isKlarnaOneAvailable && !hasNonKlarnaOneProductsSelected;
    const compatibleOneProducts = filteredProducts.filter(
      p => p.id.includes('one') && p.isCompatible,
    );
    const shouldShowExpandedProductCard = compatibleOneProducts.length === 1;
    const translationSuffix = isKlarnaOne && shouldShowExpandedProductCard ? '_kco_hidden' : '';

    return (
      <ContainerTemplate
        title={
          isKustomPartner
            ? t(`${tPrefix}.kustom.title`)
            : t(`${tPrefix}.title${translationSuffix}_line_1`)
        }
        subtitle={
          isKustomPartner
            ? t(`${tPrefix}.kustom.subtitle${translationSuffix}`)
            : t(`${tPrefix}.subtitle${translationSuffix}`)
        }
        error={error || setupError}
        showRecaptchaFooter
      >
        <ProductSelectionWrapper
          t={t}
          onChange={this.onSelect}
          country={countryKey}
          products={filteredProducts}
          isSelectedFn={this.isSelectedFn}
          extraAttributesToProductFn={product =>
            isRunningContextCypress(window)
              ? {
                  'data-is-selected': product.isSelected ? 'true' : 'false',
                  ...generateCyElemId(CY_PRODUCT_TO_CONTAINER_IDS_MAP[product.id], window),
                }
              : {}
          }
          isKlarnaOne={isKlarnaOne}
          shouldShowExpandedProductCard={shouldShowExpandedProductCard}
        />
        {this.renderButtons()}
      </ContainerTemplate>
    );
  }
}

SignupProductSelection.propTypes = {
  ...defaultContainerPropTypes,
  basePath: PropTypes.string.isRequired,
  currencyFormatter: PropTypes.func.isRequired,
  dateFormatter: PropTypes.object.isRequired,
  hasNonKlarnaOneProductsSelected: PropTypes.bool.isRequired,
  history: PropTypes.object.isRequired,
  isKlarnaOneAvailable: PropTypes.bool.isRequired,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      isSelected: PropTypes.bool,
      prices: PropTypes.arrayOf(PropTypes.object),
    }),
  ).isRequired,
  productsFromQuery: PropTypes.array.isRequired,
  setProducts: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  onChangeFormValue: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  countryKey: PropTypes.string,
  isKustomPartner: PropTypes.bool,
  platformKey: PropTypes.string,
  selectedCountry: PropTypes.object,
  selectedPlatform: PropTypes.object,
};

export default compose(
  connect(
    state => ({
      countryKey: getSelectedCountryKey(state),
      currencyFormatter: magei18nSelectors.formatters.currency(state),
      dateFormatter: magei18nSelectors.formatters.date(state),
      hasNonKlarnaOneProductsSelected: hasNonKlarnaOneProducts(state),
      isKlarnaOneAvailable: isKlarnaOneAvailable(state),
      isKustomPartner: isKustomPartner(state),
      platformKey: getSelectedPlatformKey(state),
      products: getCompatibleProductsFlattened(state),
      productsFromQuery: getProductsFromQuery(state),
      selectedCountry: getSelectedCountry(state),
      selectedPlatform: getSelectedPlatform(state),
      setupError: getSetupError(state),
      shouldShowContactSalesButton: shouldShowContactSalesButton(state),
      t: translator(state),
    }),
    {
      onChangeFormValue: a.onChangeFormValue,
      setProducts: a.setProductsCreator,
    },
  ),
)(withResponsive(SignupProductSelection));
